// ── 人才雷达：跨项目聚合团队人才背景信号 ──────────────────────────────
// 两个数据源都覆盖：
//   1. project_cache 里 enhancedTeam.topContributors 的 pedigreeSignal（已上线
//      项目，见 teamEnhanced.js/founderPedigree.js）——但这张表走的是浏览器直连
//      GitHub 的未认证请求，覆盖率很低（本来就没多少已分析项目，其中大部分还
//      没抓到贡献者数据）。
//   2. earlybird_projects 里 github_top_contributors 的 pedigreeSignal（早期
//      雷达项目，走后端 token 的批量脚本 enrich-earlybird-github.mjs +
//      scripts/backfill-earlybird-pedigree.mjs 补的 pedigreeSignal）——覆盖率
//      高两个数量级（600+ 项目 vs 个位数），是目前人才雷达真正有内容可看的
//      主要来源。
// 两边都按 GitHub login（key）聚合到同一个 Map 里——同一个人如果在两边都有
// 贡献记录，会自然合并成一条"跨项目参与度"更高的记录。
//
// 实现方式：全表扫两张表，客户端按人聚合。这是故意选择的简单做法，不是遗漏——
// Supabase JS 客户端做"jsonb 数组里某个元素的子字段非空"这种过滤不方便，成本
// 是每次进页面都要拉全表。如果规模涨到影响体验，再考虑加一张物化的聚合表或用
// RPC 做服务端过滤。
import { supabase } from '../lib/supabase';

/**
 * @typedef {{ project: string, type: 'elite_institution'|'repeat_founder'|'both', evidence: string }} TalentHit
 * @typedef {{ key: string, name: string, username: string|null, hits: TalentHit[] }} TalentEntry
 */

function addHit(byPerson, key, name, username, hit) {
  if (!key) return;
  if (!byPerson.has(key)) byPerson.set(key, { key, name, username, hits: [] });
  byPerson.get(key).hits.push(hit);
}

/**
 * @returns {Promise<TalentEntry[]>} 按命中次数（= 跨项目参与度）降序排列
 */
export async function fetchTalentRadar() {
  if (!supabase) return [];

  const [cacheRes, earlybirdRes] = await Promise.all([
    supabase.from('project_cache').select('project_name, raw_data').gt('expires_at', new Date().toISOString()),
    supabase.from('earlybird_projects').select('project_name, github_top_contributors').not('github_top_contributors', 'is', null),
  ]);

  const byPerson = new Map();

  if (!cacheRes.error && Array.isArray(cacheRes.data)) {
    for (const row of cacheRes.data) {
      const contributors = row.raw_data?.enhancedTeam?.topContributors || [];
      for (const c of contributors) {
        if (!c.pedigreeSignal) continue;
        addHit(byPerson, c.username || c.name, c.name, c.username || null, {
          project: row.project_name,
          type: c.pedigreeSignal.type,
          evidence: c.pedigreeSignal.evidence,
        });
      }
    }
  }

  if (!earlybirdRes.error && Array.isArray(earlybirdRes.data)) {
    for (const row of earlybirdRes.data) {
      const contributors = row.github_top_contributors || [];
      for (const c of contributors) {
        if (!c.pedigreeSignal) continue;
        addHit(byPerson, c.login || c.name, c.name || c.login, c.login || null, {
          project: row.project_name,
          type: c.pedigreeSignal.type,
          evidence: c.pedigreeSignal.evidence,
        });
      }
    }
  }

  return [...byPerson.values()].sort((a, b) => b.hits.length - a.hits.length);
}
